// frontend/src/pages/admin/AdminCompanyList.tsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaTrash, FaUsers, FaListUl } from 'react-icons/fa';

interface Discounts {
  employee_first: number | null;
  employee_followup: number | null;
  family_first: number | null;
  family_followup: number | null;
  friend_first: number | null;
  friend_followup: number | null;
}

interface Company {
  id: number;
  name: string;
  code: string;
  email_domains: string[];
  friend_referral_coupon_id: number | null;
  family_limit: number;
  is_active: boolean;
  member_count: number;
  discounts: Discounts;
}

interface Member {
  id: number;
  user_id: number;
  member_type: string;
  is_active: boolean;
  email: string | null;
  name: string | null;
}

const AdminCompanyList: React.FC = () => {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);

  // 編集ダイアログ
  const [editTarget, setEditTarget] = useState<Company | null>(null);
  const [editName, setEditName] = useState('');
  const [editCode, setEditCode] = useState('');
  const [editFriendCouponId, setEditFriendCouponId] = useState('');
  const [editFamilyLimit, setEditFamilyLimit] = useState('5');
  const [editActive, setEditActive] = useState(true);
  const [creating, setCreating] = useState(false);
  // 割引率
  const [discEmpFirst,    setDiscEmpFirst]    = useState('');
  const [discEmpFollowup, setDiscEmpFollowup] = useState('');
  const [discFamFirst,    setDiscFamFirst]    = useState('');
  const [discFamFollowup, setDiscFamFollowup] = useState('');
  const [discFriFirst,    setDiscFriFirst]    = useState('');
  const [discFriFollowup, setDiscFriFollowup] = useState('');

  // メンバー一覧ダイアログ
  const [memberCompany, setMemberCompany] = useState<Company | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [addUserId, setAddUserId] = useState('');
  const [addMemberType, setAddMemberType] = useState('employee');

  // 名簿ダイアログ
  const [rosterCompany, setRosterCompany] = useState<Company | null>(null);
  const [rosterText, setRosterText] = useState('');
  const [rosterStats, setRosterStats] = useState<{ total: number; claimed: number; unclaimed: number; dob_set: number } | null>(null);
  const [rosterEntries, setRosterEntries] = useState<Array<{ employee_no: string; has_dob?: boolean; claimed_by?: { name?: string | null; email?: string | null } | null; claimed_at?: string | null }>>([]);
  // CSV から読み込んだ生年月日を「非表示」で一時保持(textareaには番号のみ → 生年月日をUIに晒さない)。
  // 送信時に社員番号へ紐付ける。手入力で追加した番号は生年月日なし=既存を保持(バックエンド仕様)。
  const [pendingCsvDob, setPendingCsvDob] = useState<Record<string, string>>({});
  // 社員番号を自然順(数字感知)でソート。文字列カラムのため素の昇順だと 1,101,2… になる問題を回避
  // (1,2,10,101 の順。E001 等の英数字混在も自然に並ぶ)。
  const sortByEmpNo = <T extends { employee_no: string }>(arr: T[]): T[] =>
    arr.slice().sort((a, b) => String(a.employee_no).localeCompare(String(b.employee_no), undefined, { numeric: true }));

  // CSVファイルを読み込み、「社員番号,生年月日」形式でテキストエリアに展開（内容確認後に全量置き換え）
  const handleCsvFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';  // 同じファイルを再選択できるようにリセット
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      const nums: string[] = [];
      const dobMap: Record<string, string> = {};
      text.split(/\r?\n/).forEach((line, idx) => {
        const cols = line.split(',').map(c => c.trim().replace(/^["']|["']$/g, ''));
        const no = cols[0];
        if (!no) return;
        // 先頭行が見出しならスキップ:①見出し語を含む、または②2列目(生年月日位置)が存在するのに
        // 数字を含まない(「生年月日」「birth_date」やShift-JIS文字化けの見出し)場合。
        if (idx === 0 && (/社員|employee|番号|氏名|name/i.test(no) || (cols[1] && !/\d/.test(cols[1])))) return;
        nums.push(no);
        const birth = (cols[1] || '').trim();
        if (birth) dobMap[no] = birth;
      });
      // ★生年月日はUIに出さず一時保持。textareaには社員番号のみ表示。
      setRosterText(nums.join('\n'));
      setPendingCsvDob(dobMap);
      const dobCount = Object.keys(dobMap).length;
      toast.success(`CSVから ${nums.length} 件を読み込みました(生年月日 ${dobCount} 件・非表示)。「全量置き換え」で反映します`);
    };
    reader.onerror = () => toast.error('CSVの読み込みに失敗しました');
    reader.readAsText(file);
  };
  const [rosterBusy, setRosterBusy] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/admin/companies');
      setCompanies(res.data);
    } catch {
      toast.error('法人一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resetDiscounts = (d?: Discounts) => {
    setDiscEmpFirst(d?.employee_first != null ? String(d.employee_first) : '');
    setDiscEmpFollowup(d?.employee_followup != null ? String(d.employee_followup) : '');
    setDiscFamFirst(d?.family_first != null ? String(d.family_first) : '');
    setDiscFamFollowup(d?.family_followup != null ? String(d.family_followup) : '');
    setDiscFriFirst(d?.friend_first != null ? String(d.friend_first) : '');
    setDiscFriFollowup(d?.friend_followup != null ? String(d.friend_followup) : '');
  };

  const openCreate = () => {
    setEditTarget(null);
    setEditName(''); setEditCode('');
    setEditFriendCouponId(''); setEditFamilyLimit('5'); setEditActive(true);
    resetDiscounts(); setCreating(true);
  };

  const openEdit = (c: Company) => {
    setEditTarget(c);
    setEditName(c.name); setEditCode(c.code);
    setEditFriendCouponId(c.friend_referral_coupon_id ? String(c.friend_referral_coupon_id) : '');
    setEditFamilyLimit(c.family_limit != null ? String(c.family_limit) : '5');
    setEditActive(c.is_active);
    resetDiscounts(c.discounts); setCreating(true);
  };

  const save = async () => {
    const toNum = (s: string) => s.trim() !== '' ? Number(s) : null;
    const body = {
      name: editName,
      code: editCode,
      friend_referral_coupon_id: editFriendCouponId ? Number(editFriendCouponId) : null,
      family_limit: editFamilyLimit.trim() !== '' ? Math.max(0, Number(editFamilyLimit)) : 5,
      is_active: editActive,
      discounts: {
        employee_first:    toNum(discEmpFirst),
        employee_followup: toNum(discEmpFollowup),
        family_first:      toNum(discFamFirst),
        family_followup:   toNum(discFamFollowup),
        friend_first:      toNum(discFriFirst),
        friend_followup:   toNum(discFriFollowup),
      },
    };
    try {
      if (editTarget) {
        await api.patch(`/admin/companies/${editTarget.id}`, body);
        toast.success('更新しました');
      } else {
        await api.post('/admin/companies', body);
        toast.success('作成しました');
      }
      setCreating(false);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    }
  };

  const deleteCompany = async (c: Company) => {
    if (!window.confirm(`「${c.name}」を削除しますか？`)) return;
    try {
      await api.delete(`/admin/companies/${c.id}`);
      toast.success('削除しました');
      load();
    } catch {
      toast.error('削除に失敗しました');
    }
  };

  const openMembers = async (c: Company) => {
    setMemberCompany(c);
    const res = await api.get(`/admin/companies/${c.id}/members`);
    setMembers(res.data);
    setAddUserId(''); setAddMemberType('employee');
  };

  const addMember = async () => {
    if (!memberCompany || !addUserId) return;
    try {
      await api.post(`/admin/companies/${memberCompany.id}/members`, {
        user_id: Number(addUserId),
        member_type: addMemberType,
      });
      toast.success('メンバーを追加しました');
      const res = await api.get(`/admin/companies/${memberCompany.id}/members`);
      setMembers(res.data);
      setAddUserId('');
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || '追加に失敗しました');
    }
  };

  const changeMemberType = async (memberId: number, memberType: string) => {
    if (!memberCompany) return;
    try {
      await api.patch(`/admin/companies/${memberCompany.id}/members/${memberId}`, { member_type: memberType });
      const res = await api.get(`/admin/companies/${memberCompany.id}/members`);
      setMembers(res.data);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || '区分の変更に失敗しました');
    }
  };

  const openRoster = async (c: Company) => {
    setRosterCompany(c);
    setRosterText('');
    setRosterStats(null);
    setRosterEntries([]);
    setPendingCsvDob({});
    try {
      const res = await api.get(`/admin/companies/${c.id}/roster`);
      setRosterStats({ total: res.data.total, claimed: res.data.claimed, unclaimed: res.data.unclaimed, dob_set: res.data.dob_set ?? 0 });
      // 既存は社員番号のみ展開(生年月日は非表示=UIに晒さない)。生年月日の設定/更新はCSVで。
      const sorted = sortByEmpNo(res.data.entries || []);
      setRosterText(sorted.map((e: any) => e.employee_no).join('\n'));
      setRosterEntries(sorted);
    } catch {
      toast.error('名簿の取得に失敗しました');
    }
  };

  const saveRoster = async () => {
    if (!rosterCompany) return;
    // 1行 = 「社員番号,生年月日」（生年月日は任意。カンマ以降を生年月日として扱う）
    const entries = rosterText.split(/\n/).map(line => {
      const parts = line.split(',').map(s => s.trim());
      const employee_no = parts[0];
      // 生年月日: 直接貼付(番号,生年月日) > CSV読込の非表示分 > null(未指定=既存を保持)。
      const inlineDob = parts.slice(1).join('').trim();
      const birth_date = inlineDob || pendingCsvDob[employee_no] || null;
      return { employee_no, birth_date };
    }).filter(e => e.employee_no);
    // 0件＝全社員失効。誤ったCSV(文字コード/区切り)での事故を防ぐため、より強い確認＋明示フラグを要求。
    const allowEmpty = entries.length === 0;
    if (allowEmpty) {
      if (!window.confirm(`⚠️ 名簿が空(0件)です。このまま保存すると認証済みの全社員が失効します。\nCSVの文字コード(UTF-8)・区切り文字をご確認ください。\n本当に全件削除しますか？`)) return;
    } else {
      if (!window.confirm(`名簿を全${entries.length}件で全量置き換えします。\nリストから外れた社員番号の社員資格は失効します。よろしいですか？`)) return;
    }
    setRosterBusy(true);
    try {
      const res = await api.put(`/admin/companies/${rosterCompany.id}/roster`, { entries, allow_empty: allowEmpty });
      const s = res.data;
      toast.success(`名簿を更新しました（追加${s.added}・削除${s.removed}・失効${s.revoked_users}名）`);
      if (s.dob_rejected > 0) {
        // 生年月日が形式不正で取り込めなかった行を可視化（黙って単因子に戻さない）
        toast.error(`生年月日${s.dob_rejected}件は形式不正のため取り込めませんでした（例: 1990-01-15 / 19900115）。該当社員は社員番号のみの認証になります。`);
      }
      const r = await api.get(`/admin/companies/${rosterCompany.id}/roster`);
      setRosterStats({ total: r.data.total, claimed: r.data.claimed, unclaimed: r.data.unclaimed, dob_set: r.data.dob_set ?? 0 });
      const sorted = sortByEmpNo(r.data.entries || []);
      setRosterEntries(sorted);
      setRosterText(sorted.map((e: any) => e.employee_no).join('\n'));  // 保存後も自然順の番号一覧に正規化(左右一致)
      setPendingCsvDob({});  // 反映済み → 一時保持クリア
      // 生年月日 未設定の番号は「社員番号のみ」で認証可能=第二因子なし → 明示的に警告
      const noDob = (r.data.total ?? 0) - (r.data.dob_set ?? 0);
      if (noDob > 0) {
        toast.warn(`${noDob}件が生年月日 未設定です(社員番号のみで認証可能)。なりすまし防止のため、CSVに生年月日を含めることを推奨します。`);
      }
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || '名簿の更新に失敗しました');
    } finally {
      setRosterBusy(false);
    }
  };

  const removeMember = async (memberId: number) => {
    if (!memberCompany) return;
    try {
      await api.delete(`/admin/companies/${memberCompany.id}/members/${memberId}`);
      const res = await api.get(`/admin/companies/${memberCompany.id}/members`);
      setMembers(res.data);
      load();
    } catch {
      toast.error('削除に失敗しました');
    }
  };

  if (loading) return <div className="p-8 text-gray-400">読み込み中...</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">法人管理</h1>
        <button onClick={openCreate} className="flex items-center gap-2 bg-coral text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-coral/90">
          <FaPlus /> 新規法人
        </button>
      </div>

      {/* 法人一覧 */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {/* ▼ モバイル: カード表示（横スクロールバーを出さずに縦積み） */}
        <div className="md:hidden divide-y divide-gray-100">
          {companies.map(c => (
            <div key={c.id} className="p-4">
              {/* 法人名 + 状態 */}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-gray-800 break-words">{c.name}</div>
                  {/* コード */}
                  <div className="text-xs text-gray-500 font-mono break-all mt-0.5">コード：{c.code}</div>
                  <button
                    onClick={() => {
                      const url = `${window.location.origin}/corp/${c.code}`;
                      if (navigator.clipboard?.writeText) {
                        navigator.clipboard.writeText(url).then(() => toast.success('社員登録リンクをコピーしました')).catch(() => window.prompt('リンクをコピー', url));
                      } else { window.prompt('リンクをコピー', url); }
                    }}
                    className="text-xs text-coral hover:underline mt-0.5"
                  >社員登録リンクをコピー</button>
                </div>
                <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {c.is_active ? '有効' : '無効'}
                </span>
              </div>

              {/* 社員数 */}
              <div className="mt-2 text-xs">
                <span className="text-gray-400">社員数：</span>
                <span className="text-gray-700">{c.member_count}</span>
              </div>

              {/* 操作 */}
              <div className="mt-3 flex items-center flex-wrap gap-4">
                <button onClick={() => openMembers(c)} className="shrink-0 flex items-center gap-1 text-blue-500 hover:text-blue-700 text-xs" title="メンバー管理"><FaUsers /> メンバー</button>
                <button onClick={() => openRoster(c)} className="shrink-0 flex items-center gap-1 text-emerald-500 hover:text-emerald-700 text-xs" title="社員番号名簿"><FaListUl /> 名簿</button>
                <button onClick={() => openEdit(c)} className="shrink-0 flex items-center gap-1 text-gray-500 hover:text-gray-700 text-xs" title="編集"><FaEdit /> 編集</button>
                <button onClick={() => deleteCompany(c)} className="shrink-0 flex items-center gap-1 text-red-400 hover:text-red-600 text-xs" title="削除"><FaTrash /> 削除</button>
              </div>
            </div>
          ))}
          {companies.length === 0 && (
            <div className="p-8 text-center text-gray-400 text-sm">法人が登録されていません</div>
          )}
        </div>

        {/* ▼ デスクトップ: テーブル表示 */}
        <table className="w-full text-sm hidden md:table">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="px-4 py-3 text-left">法人名</th>
              <th className="px-4 py-3 text-left">コード</th>
              <th className="px-4 py-3 text-center">社員数</th>
              <th className="px-4 py-3 text-center">状態</th>
              <th className="px-4 py-3 text-center">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {companies.map(c => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-800">{c.name}</td>
                <td className="px-4 py-3 text-gray-500 font-mono">
                  {c.code}
                  <button
                    onClick={() => {
                      const url = `${window.location.origin}/corp/${c.code}`;
                      if (navigator.clipboard?.writeText) {
                        navigator.clipboard.writeText(url).then(() => toast.success('社員登録リンクをコピーしました')).catch(() => window.prompt('リンクをコピー', url));
                      } else { window.prompt('リンクをコピー', url); }
                    }}
                    className="block text-xs text-coral hover:underline font-sans mt-0.5"
                  >リンクをコピー</button>
                </td>
                <td className="px-4 py-3 text-center">{c.member_count}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {c.is_active ? '有効' : '無効'}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <button onClick={() => openMembers(c)} className="text-blue-500 hover:text-blue-700" title="メンバー管理"><FaUsers /></button>
                    <button onClick={() => openRoster(c)} className="text-emerald-500 hover:text-emerald-700" title="社員番号名簿"><FaListUl /></button>
                    <button onClick={() => openEdit(c)} className="text-gray-500 hover:text-gray-700" title="編集"><FaEdit /></button>
                    <button onClick={() => deleteCompany(c)} className="text-red-400 hover:text-red-600" title="削除"><FaTrash /></button>
                  </div>
                </td>
              </tr>
            ))}
            {companies.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">法人が登録されていません</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 作成/編集ダイアログ */}
      {creating && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold mb-4">{editTarget ? '法人を編集' : '新規法人を作成'}</h2>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">法人名 *</label>
                <input value={editName} onChange={e => setEditName(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="例: SMHC株式会社" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">法人コード * （大文字英数字）</label>
                <input value={editCode} onChange={e => setEditCode(e.target.value.toUpperCase())}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono" placeholder="例: SMHC2026"
                  disabled={!!editTarget} />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">友人紹介クーポンID（任意）</label>
                <input value={editFriendCouponId} onChange={e => setEditFriendCouponId(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="例: 42" type="number" />
                <p className="text-xs text-gray-400 mt-1">社員が紹介した友人に付与するクーポンID</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">家族登録の上限（社員1人あたり）</label>
                <input value={editFamilyLimit} onChange={e => setEditFamilyLimit(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="例: 5" type="number" min="0" />
                <p className="text-xs text-gray-400 mt-1">上限を超えた家族紹介は自動的に「友人」として登録されます</p>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editActive} onChange={e => setEditActive(e.target.checked)} id="chk-active" />
                <label htmlFor="chk-active" className="text-sm text-gray-700">有効</label>
              </div>

              {/* 割引率設定 */}
              <div className="pt-3 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 mb-2">割引率設定（%、空白=適用なし）</p>
                {[
                  { label: '社員',   first: discEmpFirst,    setFirst: setDiscEmpFirst,    followup: discEmpFollowup,    setFollowup: setDiscEmpFollowup },
                  { label: '家族',   first: discFamFirst,    setFirst: setDiscFamFirst,    followup: discFamFollowup,    setFollowup: setDiscFamFollowup },
                  { label: '友人',   first: discFriFirst,    setFirst: setDiscFriFirst,    followup: discFriFollowup,    setFollowup: setDiscFriFollowup },
                ].map(row => (
                  <div key={row.label} className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-gray-600 w-8 shrink-0">{row.label}</span>
                    <div className="flex items-center gap-1 flex-1">
                      <span className="text-xs text-gray-400">初回</span>
                      <input
                        type="number" min="0" max="100"
                        value={row.first}
                        onChange={e => row.setFirst(e.target.value)}
                        className="w-16 border border-gray-200 rounded px-2 py-1 text-sm text-center"
                        placeholder="—"
                      />
                      <span className="text-xs text-gray-400">%</span>
                    </div>
                    <div className="flex items-center gap-1 flex-1">
                      <span className="text-xs text-gray-400">継続</span>
                      <input
                        type="number" min="0" max="100"
                        value={row.followup}
                        onChange={e => row.setFollowup(e.target.value)}
                        className="w-16 border border-gray-200 rounded px-2 py-1 text-sm text-center"
                        placeholder="—"
                      />
                      <span className="text-xs text-gray-400">%</span>
                    </div>
                  </div>
                ))}
                <p className="text-xs text-gray-400 mt-1">保存すると対応クーポンが自動作成されます</p>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setCreating(false)} className="flex-1 border border-gray-200 text-gray-600 rounded-lg py-2 text-sm">キャンセル</button>
              <button onClick={save} className="flex-1 bg-coral text-white rounded-lg py-2 text-sm font-medium">保存</button>
            </div>
          </div>
        </div>
      )}

      {/* メンバー管理ダイアログ */}
      {memberCompany && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <h2 className="text-lg font-bold mb-1">{memberCompany.name} — メンバー管理</h2>
            <p className="text-xs text-gray-400 mb-4">※ 社員認証は「社員番号名簿」で行います</p>

            {/* メンバー追加 */}
            <div className="flex gap-2 mb-4">
              <input value={addUserId} onChange={e => setAddUserId(e.target.value)}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm" placeholder="ユーザーID" type="number" />
              <select value={addMemberType} onChange={e => setAddMemberType(e.target.value)}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
                <option value="employee">社員</option>
                <option value="family">家族</option>
                <option value="friend">友人</option>
              </select>
              <button onClick={addMember} className="bg-coral text-white px-3 py-1.5 rounded-lg text-sm font-medium">追加</button>
            </div>

            {/* メンバー一覧 */}
            <div className="divide-y divide-gray-100">
              {members.map(m => (
                <div key={m.id} className={`flex items-center justify-between py-2 ${!m.is_active ? 'opacity-40' : ''}`}>
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-gray-800">{m.name || `User #${m.user_id}`}</span>
                    <span className="text-xs text-gray-400 ml-2">{m.email}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {/* 区分を手動変更（管理者は上限の対象外） */}
                    <select
                      value={m.member_type}
                      onChange={e => changeMemberType(m.id, e.target.value)}
                      disabled={!m.is_active}
                      className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white"
                    >
                      <option value="employee">社員</option>
                      <option value="family">家族</option>
                      <option value="friend">友人</option>
                    </select>
                    {m.is_active && (
                      <button onClick={() => removeMember(m.id)} className="text-red-400 hover:text-red-600 text-xs">削除</button>
                    )}
                  </div>
                </div>
              ))}
              {members.length === 0 && <p className="text-sm text-gray-400 py-3">メンバーがいません</p>}
            </div>

            <button onClick={() => setMemberCompany(null)} className="w-full mt-4 border border-gray-200 text-gray-600 rounded-lg py-2 text-sm">閉じる</button>
          </div>
        </div>
      )}

      {/* 社員番号名簿ダイアログ */}
      {rosterCompany && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-3xl max-h-[85vh] overflow-y-auto">
            <h2 className="text-lg font-bold mb-1">{rosterCompany.name} — 社員番号名簿</h2>
            <p className="text-xs text-gray-400 mb-3">
              社員名簿は <span className="font-mono">CSV(社員番号,生年月日)</span> で取り込みます。🔒 生年月日はセキュリティのため保存後は非表示です(設定・更新はCSVで。番号のみで再置換した場合は既存の生年月日を保持)。「全量置き換え」で更新します。
              リストから外れた番号の社員資格は自動的に失効します（アカウントは残ります）。
            </p>
            {rosterStats && (
              <div className="flex flex-wrap gap-2 mb-3 text-xs">
                <span className="bg-gray-100 rounded px-2 py-1">登録 {rosterStats.total}</span>
                <span className="bg-emerald-50 text-emerald-700 rounded px-2 py-1">認証済 {rosterStats.claimed}</span>
                <span className="bg-amber-50 text-amber-700 rounded px-2 py-1">未認証 {rosterStats.unclaimed}</span>
                <span className="bg-blue-50 text-blue-700 rounded px-2 py-1">生年月日 {rosterStats.dob_set}/{rosterStats.total}</span>
              </div>
            )}
            <div className="mb-2 flex items-center gap-2 flex-wrap">
              <label className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                📄 CSVを読み込む
                <input type="file" accept=".csv,.txt" onChange={handleCsvFile} className="hidden" />
              </label>
              <span className="text-[11px] text-gray-400">CSV列: 社員番号, 生年月日</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 左: 名簿の編集 */}
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-1">名簿の編集（社員番号のみ・🔒生年月日は非表示）</p>
                <textarea
                  value={rosterText}
                  onChange={e => setRosterText(e.target.value)}
                  rows={14}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono"
                  placeholder={'E001\nE002\nE003'}
                />
                <p className="text-xs text-gray-400 mt-1">
                  現在の入力件数: {rosterText.split(/\n/).map(s => s.trim()).filter(Boolean).length} 件
                </p>
              </div>

              {/* 右: 社員番号 ↔ 認証した本人（読み取り専用） */}
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-1">認証状況（誰が認証したか）</p>
                <div className="h-[22rem] overflow-y-auto divide-y divide-gray-50 border border-gray-100 rounded-lg">
                  {rosterEntries.length > 0 ? rosterEntries.map((e) => (
                    <div key={e.employee_no} className="flex items-start justify-between gap-2 px-3 py-1.5 text-xs">
                      <span className="font-mono text-gray-700 shrink-0">
                        {e.employee_no}
                        {e.has_dob
                          ? <span className="text-gray-300 ml-1" title="生年月日 登録済（非表示）">🔒</span>
                          : <span className="text-amber-500 ml-1" title="生年月日 未登録">⚠</span>}
                      </span>
                      {e.claimed_by ? (
                        <span className="min-w-0 flex flex-col items-end text-right">
                          <span className="whitespace-nowrap">
                            <span className="text-emerald-600 font-medium">✅ 認証済</span>
                            <span className="text-gray-800 ml-1">{e.claimed_by.name || '（氏名未登録）'}</span>
                          </span>
                          {e.claimed_by.email && (
                            <span className="text-gray-400 text-[11px] max-w-[13rem] truncate" title={e.claimed_by.email}>{e.claimed_by.email}</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-amber-600 shrink-0">⏳ 未認証</span>
                      )}
                    </div>
                  )) : (
                    <p className="p-3 text-xs text-gray-400">保存すると認証状況がここに表示されます</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-4">
              <button onClick={() => setRosterCompany(null)} className="flex-1 border border-gray-200 text-gray-600 rounded-lg py-2 text-sm">閉じる</button>
              <button onClick={saveRoster} disabled={rosterBusy}
                className="flex-1 bg-coral text-white rounded-lg py-2 text-sm font-medium disabled:opacity-50">
                {rosterBusy ? '...' : '全量置き換え'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCompanyList;
